import { useCallback, useEffect, useState } from "react";

type FieldValue = string | boolean | undefined;

/**
 * `"session"` (the default) keeps values for the current tab only.
 *
 * `"local"` additionally mirrors them to localStorage so a visitor returning in a new
 * tab or on a later day finds their answers still filled in. Reads prefer sessionStorage
 * so same-visit edits always win over an older stored answer. Both modes use the same
 * key, which is what lets one form pre-fill from another form's fields.
 */
export type FormFieldStorage = "session" | "local";

// Custom event name used to synchronise hook instances across components
// when a value is written to session storage within the same tab.
const SYNC_EVENT = "form-field-persistence-sync";

// Helper to normalize phone numbers to E.164 format
const normalizePhoneValue = (value: string | undefined): string => {
  if (!value) return "";
  return value.replace(/[^\d+]/g, "");
};

// Type-safe value getters
const getStringValue = (value: FieldValue): string => {
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "true" : "false";
  return "";
};

const getBooleanValue = (value: FieldValue): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string")
    return (
      value === "true" || value === "1" || value === "yes" || value === "on"
    );
  return false;
};

/**
 * Clear persisted values for the given field IDs and notify any mounted
 * hook instances so they reset to their initial value too.
 */
export const clearPersistedFormFields = (fieldIds: string[]) => {
  if (typeof window === "undefined") return;
  for (const fieldId of fieldIds) {
    const storageKey = `form-field-${fieldId}`;
    try {
      sessionStorage.removeItem(storageKey);
      localStorage.removeItem(storageKey);
      window.dispatchEvent(
        new CustomEvent(SYNC_EVENT, { detail: { key: storageKey } }),
      );
    } catch (error) {
      console.warn(`Failed to clear persisted value for ${fieldId}:`, error);
    }
  }
};

/**
 * Seed persisted values for fields the visitor has already answered in an earlier
 * session, and notify mounted hook instances so they pick them up.
 *
 * Seeding storage rather than passing an initial value is deliberate: a cleared
 * field removes its key, so any non-empty initial value would immediately be
 * restored and the field could never be emptied. Existing entries are left alone.
 */
export const seedPersistedFormFields = (values: Record<string, string>) => {
  if (typeof window === "undefined") return;
  for (const [fieldId, value] of Object.entries(values)) {
    if (!value) continue;
    const storageKey = `form-field-${fieldId}`;
    try {
      if (sessionStorage.getItem(storageKey) !== null) continue;
      sessionStorage.setItem(storageKey, value);
      window.dispatchEvent(
        new CustomEvent(SYNC_EVENT, { detail: { key: storageKey } }),
      );
    } catch (error) {
      console.warn(`Failed to seed persisted value for ${fieldId}:`, error);
    }
  }
};

/**
 * Custom hook to persist form field values across the entire site using session storage.
 * Values are shared between all forms on the site based on field ID.
 */
export const useFormFieldPersistence = (
  fieldId: string,
  initialValue: FieldValue = "",
  fieldType: "text" | "select" | "checkbox" | "phone" = "text",
  storage: FormFieldStorage = "session",
) => {
  const [value, setValue] = useState<FieldValue>(initialValue);
  const [isHydrated, setIsHydrated] = useState(false);

  // Storage key format: form-field-{fieldId}
  const storageKey = `form-field-${fieldId}`;

  // Get persisted value from session storage (SSR-safe)
  const getPersistedValue = useCallback((): FieldValue => {
    if (typeof window === "undefined") return initialValue;

    try {
      let storedValue = sessionStorage.getItem(storageKey);

      if (storedValue === null && storage === "local") {
        storedValue = localStorage.getItem(storageKey);
        // Promote into the session so consumers that read this key with the default
        // `"session"` storage see it too. Without this a returning visitor's restored
        // values are visible to the form but invisible to everything downstream of it
        // (lead routing, prefill on other forms), which read session-only.
        if (storedValue !== null)
          sessionStorage.setItem(storageKey, storedValue);
      }

      if (storedValue === null) return initialValue;

      // Handle different field types
      switch (fieldType) {
        case "checkbox":
          return (
            storedValue === "true" ||
            storedValue === "1" ||
            storedValue === "yes" ||
            storedValue === "on"
          );
        case "phone":
          return normalizePhoneValue(storedValue);
        case "text":
        case "select":
        default:
          return storedValue;
      }
    } catch (error) {
      console.warn(`Failed to retrieve persisted value for ${fieldId}:`, error);
      return initialValue;
    }
  }, [storageKey, initialValue, fieldType, fieldId, storage]);

  // Save value to session storage
  const persistValue = useCallback(
    (newValue: FieldValue) => {
      if (typeof window === "undefined") return;

      try {
        // For checkboxes, false is a valid state that should be persisted
        const shouldRemove =
          fieldType === "checkbox"
            ? newValue === undefined
            : newValue === undefined || newValue === "" || newValue === false;

        if (shouldRemove) {
          // Remove empty values to keep storage clean
          sessionStorage.removeItem(storageKey);
          if (storage === "local") localStorage.removeItem(storageKey);
        } else {
          // Normalize phone values before storing
          const valueToStore =
            fieldType === "phone"
              ? normalizePhoneValue(String(newValue))
              : String(newValue);
          sessionStorage.setItem(storageKey, valueToStore);
          if (storage === "local")
            localStorage.setItem(storageKey, valueToStore);
        }

        // Notify other hook instances watching the same key so they re-read
        window.dispatchEvent(
          new CustomEvent(SYNC_EVENT, { detail: { key: storageKey } }),
        );
      } catch (error) {
        console.warn(`Failed to persist value for ${fieldId}:`, error);
      }
    },
    [storageKey, fieldId, fieldType, storage],
  );

  // Update value and persist it
  const updateValue = useCallback(
    (newValue: FieldValue) => {
      setValue(newValue);
      persistValue(newValue);
    },
    [persistValue],
  );

  // Hydrate with persisted value on mount
  useEffect(() => {
    const persistedValue = getPersistedValue();
    setValue(persistedValue);
    setIsHydrated(true);
  }, [getPersistedValue]);

  // Re-read session storage when another hook instance writes to the same key
  useEffect(() => {
    const handleSync = (e: Event) => {
      const { key } = (e as CustomEvent<{ key: string }>).detail;
      if (key === storageKey) {
        setValue(getPersistedValue());
      }
    };

    window.addEventListener(SYNC_EVENT, handleSync);
    return () => window.removeEventListener(SYNC_EVENT, handleSync);
  }, [storageKey, getPersistedValue]);

  return {
    value: isHydrated ? value : initialValue, // Use initial value until hydrated
    updateValue,
    isHydrated,
    // Type-safe getters
    stringValue: getStringValue(isHydrated ? value : initialValue),
    booleanValue: getBooleanValue(isHydrated ? value : initialValue),
  };
};
